/**
 * The share name of the receipt (thermal) printer for this workstation.
 *
 * The canonical source is the "Devices" registry (`connected_devices`): the
 * device flagged as the receipt printer. The legacy `printer_share_name` key is
 * kept as a fallback for machines that saved a value before the Devices page
 * existed.
 */

import { getDeviceHost, getLabelPrinter, getReceiptPrinter } from './devices';

export const PRINTER_SHARE_STORAGE_KEY = 'printer_share_name';

export function getSavedPrinterShareName(): string {
  const receipt = getReceiptPrinter();
  if (receipt?.shareName) return receipt.shareName;

  try {
    return localStorage.getItem(PRINTER_SHARE_STORAGE_KEY)?.trim() || '';
  } catch {
    return '';
  }
}

/**
 * Host (PC name or network IP) of the receipt printer, used to build the UNC
 * path `\\host\share`. Falls back to empty when the device is local.
 */
export function getReceiptPrinterHost(): string {
  return getDeviceHost(getReceiptPrinter());
}

/**
 * Share name used for specimen (collection) labels. Prefers the device
 * explicitly designated as the label printer, then falls back to the receipt
 * printer / legacy share so collection labels print to the same printer the
 * pharmacy receipts already use. Empty only when nothing is configured.
 */
export function getLabelPrinterShareName(): string {
  const label = getLabelPrinter();
  if (label?.shareName) return label.shareName.trim();
  return getSavedPrinterShareName();
}

/**
 * Host (PC name or network IP) of the collection label printer, used to build
 * the UNC path `\\host\share`. Empty when the printer is local.
 */
export function getLabelPrinterHost(): string {
  return getDeviceHost(getLabelPrinter() ?? getReceiptPrinter());
}

export function savePrinterShareName(shareName: string): void {
  const value = shareName.trim();
  try {
    if (value) localStorage.setItem(PRINTER_SHARE_STORAGE_KEY, value);
    else localStorage.removeItem(PRINTER_SHARE_STORAGE_KEY);
  } catch {
    // localStorage unavailable — ignore.
  }
}

export function clearPrinterShareName(): void {
  try {
    localStorage.removeItem(PRINTER_SHARE_STORAGE_KEY);
  } catch {
    // ignore
  }
}
